import { reverse } from '/@/utils/router';

/**
 * URL for a "recommend a hybrid for this field" link.
 *
 * Corn goes to the v2 wizard, which takes the field as a query param and opens on the
 * environment step. Soy stays on the single-page editor until the wizard's density curve and
 * defensive profile — both corn-only data — support it. Keeping that decision here means the
 * eventual soy support is a one-line change rather than a hunt through every entry point.
 */
export const seedRecommendationUrl = ({
    crop,
    fieldId,
    entrySource,
    userNodeId,
    plantingDate,
    productProfile,
    climatePattern,
    soilMoisture,
}: {
    crop: string;
    /** Omit to start at field selection; both flows otherwise open on the chosen field. */
    fieldId?: number | string;
    entrySource?: string;
    userNodeId?: number | string;
    /** Prefilled crop configuration, as handed over by the density recommendation. */
    plantingDate?: string;
    productProfile?: number | string;
    climatePattern?: number | string;
    soilMoisture?: number | string;
}) => {
    // Snake_case keys are what both flows read back off the query string.
    const qs = {
        entrySource,
        userNodeId,
        planting_date: plantingDate,
        product_profile: productProfile,
        climate_pattern: climatePattern,
        soil_moisture: soilMoisture,
    };

    if (crop === 'corn') {
        return reverse('recommendations.RecommendationFieldSelect', {
            params: { crop },
            qs: { ...qs, fieldId },
        });
    }

    // Without a field there is nothing for the editor to open on -- `reverse` would substitute
    // the missing param literally and produce `/recommendations/soy/undefined/edit/`. Soy has
    // no wizard yet, so field selection is the legacy picker.
    if (!fieldId) {
        return reverse('recommendations.RecommendationFieldSelect', { params: { crop }, qs });
    }

    return reverse('recommendations.RecommendationEdit', { params: { crop, fieldId }, qs });
};

/**
 * URL for a "recommend the management for this hybrid" link.
 *
 * The same wizard as {@link seedRecommendationUrl}, in its management flavour: the user brings
 * the hybrid, so `productProfile` is what these entry points actually carry. Corn only — the
 * management step is built on corn-only data.
 */
export const managementRecommendationUrl = ({
    fieldId,
    entrySource,
    userNodeId,
    productProfile,
    plantingDate,
    climatePattern,
    soilMoisture,
}: {
    /** Omit to start at field selection. */
    fieldId?: number | string;
    entrySource?: string;
    userNodeId?: number | string;
    productProfile?: number | string;
    plantingDate?: string;
    climatePattern?: number | string;
    soilMoisture?: number | string;
}) =>
    reverse('recommendations.ManagementRecommendationCreate', {
        qs: {
            fieldId,
            entrySource,
            userNodeId,
            // Snake_case keys are what the wizard reads back off the query string.
            product_profile: productProfile,
            planting_date: plantingDate,
            climate_pattern: climatePattern,
            soil_moisture: soilMoisture,
        },
    });

/**
 * URL for a "which of my fields suits this hybrid" link.
 *
 * The same wizard as {@link seedRecommendationUrl}, asked from the other end: the hybrid is
 * fixed, so the conditions come first and the field list is ranked by how it yields under them.
 * Corn only — the ranking endpoint behind it is corn-specific.
 */
export const fieldForHybridUrl = ({
    entrySource,
    userNodeId,
    productProfile,
}: {
    entrySource?: string;
    userNodeId?: number | string;
    productProfile?: number | string;
}) =>
    reverse('recommendations.FieldForHybridCreate', {
        qs: {
            entrySource,
            userNodeId,
            // Snake_case keys are what the wizard reads back off the query string.
            product_profile: productProfile,
        },
    });

/**
 * URL for viewing a saved seed recommendation.
 *
 * Corn opens the v2 result page; soy keeps the original read-only view, since the v2 page is
 * built on corn-only data (density curve, phenology, defensive profile). Every entry point
 * routes through here so the eventual soy support is a one-line change — and so no caller has
 * to remember the gate.
 */
export const recommendationResultUrl = ({
    recommendationId,
    crop,
    qs,
}: {
    recommendationId: number | string;
    crop: string;
    qs?: Record<string, string | undefined>;
}) => {
    const params = { recommendationId, crop };

    if (crop === 'corn') {
        return reverse('recommendations.RecommendationResultV2', { params, qs });
    }

    return reverse('recommendations.RecommendationResult', { params, qs });
};
